import React, { createContext, useContext, useState } from "react";
import { useLocalStorage } from "../../helpers/useStorage";
import ApiClient from "../../helpers/ApiClient";

/**
 * AuthProvider aligned with live shopyourtrip.com bundle.
 * Refresh: if accessToken in localStorage → getUserByauthToken.
 */

const AuthContext = createContext();
const USER_SESSION_KEY = "sytUserSession";
const ACCESS_LIST_KEY = "sytAccessList";

export function useAuthContext() {
  return useContext(AuthContext);
}

function readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(user) {
  try {
    if (user) {
      localStorage.setItem(USER_SESSION_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_SESSION_KEY);
    }
  } catch {
    // private mode
  }
}

function screensFromUser(user) {
  const screens = [];
  (user?.Screens ?? []).forEach((element) => {
    if (element?.Name) screens.push(element.Name);
  });
  return screens;
}

function writeCachedAccessList(list) {
  try {
    if (Array.isArray(list) && list.length > 0) {
      localStorage.setItem(ACCESS_LIST_KEY, JSON.stringify(list));
    } else {
      localStorage.removeItem(ACCESS_LIST_KEY);
    }
  } catch {
    // private mode
  }
}

function readCachedAccessList(user) {
  const fromUser = screensFromUser(user);
  if (fromUser.length > 0) return fromUser;
  try {
    const raw = localStorage.getItem(ACCESS_LIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

/** Live: agent only when RoleLevel === 3 */
function flagsFromUser(user) {
  const level = Number(user?.Role?.RoleLevel ?? 0);
  return {
    flag: level === 2,
    role: level === 1,
    agent: level === 3,
    adminAsUser: { status: false, id: -1 },
  };
}

function hasStoredToken() {
  try {
    const raw = localStorage.getItem("accessToken");
    if (!raw || raw === "null") return false;
    try {
      return Boolean(JSON.parse(raw));
    } catch {
      return Boolean(raw);
    }
  } catch {
    return false;
  }
}

const AuthProvider = ({ children }) => {
  const cachedUser = readCachedUser();
  const [isLogin, setLogin] = useState(() => flagsFromUser(cachedUser));
  const [user, setUserState] = useState(cachedUser);
  // Live: setAccessToken comes straight from useLocalStorage
  const [accessToken, setAccessToken] = useLocalStorage("accessToken", null);
  const [isLoading, setIsLoading] = useState(() => hasStoredToken());
  const [adminAsUser, setAdminAsUser] = useLocalStorage("extraUser", {
    status: false,
    id: -1,
  });
  const [extraUserInfo, setExtraUserInfo] = useLocalStorage(
    "extraUserInfo",
    null,
    true,
  );
  const [accessList, setAccessListState] = useState(() =>
    readCachedAccessList(cachedUser),
  );

  const setAccessList = (value) => {
    setAccessListState((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      writeCachedAccessList(Array.isArray(next) ? next : []);
      return next;
    });
  };

  // Live: setUser also writes sytUserSession
  const setUser = (value) => {
    setUserState((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      writeCachedUser(next);
      return next;
    });
  };

  const resetIsLogin = () => {
    setAccessToken(null);
    setUser(null);
    setLogin({ flag: false, role: false, agent: false });
    writeCachedUser(null);
    writeCachedAccessList([]);
    setAccessListState([]);
    setIsLoading(false);
    setAdminAsUser({ status: false, id: -1 });
    setExtraUserInfo(null);
    try {
      localStorage.removeItem(USER_SESSION_KEY);
      localStorage.removeItem(ACCESS_LIST_KEY);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("extraUserInfo");
    } catch {
      // private mode
    }
    try {
      sessionStorage.removeItem("extraUser");
    } catch {
      // private mode
    }
  };

  // Live tryLocalSignin: if accessToken (from LS hydrate) → getUserByauthToken
  const tryLocalSignin = () => {
    if (!accessToken) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    ApiClient.get("admin/getUserByauthToken")
      .then((resp) => {
        const ok =
          resp?.status === 200 || resp?.status === 304;
        if (ok && resp?.data) {
          const data = resp.data;
          setUser(data);
          const level = Number(data?.Role?.RoleLevel ?? 0);
          if (level === 1) {
            const screens = screensFromUser(data);
            if (screens.length > 0) setAccessList(screens);
            setLogin((prev) => ({
              ...prev,
              role: true,
              agent: false,
              flag: false,
            }));
          } else if (level === 3) {
            setLogin((prev) => ({
              ...prev,
              agent: true,
              role: false,
              flag: false,
            }));
          } else if (level === 2) {
            setLogin((prev) => ({
              ...prev,
              flag: true,
              role: false,
              agent: false,
            }));
          }
        } else if (resp?.status === 401 || resp?.status === 403) {
          setAccessToken(null);
          setUser(null);
          writeCachedAccessList([]);
          setAccessListState([]);
          setLogin({ flag: false, role: false, agent: false });
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  return (
    <AuthContext.Provider
      value={{
        isLogin,
        user,
        isLoading,
        setUser,
        accessToken,
        adminAsUser,
        setAccessToken,
        setAdminAsUser,
        accessList,
        extraUserInfo,
        setExtraUserInfo,
        setAccessList,
        updateAgentLoginFlag: (value) =>
          setLogin((prev) => ({ ...prev, agent: value })),
        updateLoginFlag: (value) =>
          setLogin((prev) => ({ ...prev, flag: value })),
        updateDashboardFlag: (value) =>
          setLogin((prev) => ({ ...prev, role: value })),
        resetIsLogin,
        tryLocalSignin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
